import { useState } from 'react';

interface LedProject {
  id: number;
  title: string;
  icon: string;
  description: string;
  details: string[];
  image: string;
}

const ledProjects: LedProject[] = [
  {
    id: 1,
    title: 'Tiras LED Ambientales',
    icon: 'ri-lightbulb-flash-line',
    description: 'Instalación de tiras LED RGB y monocolor en techos, muebles, escaleras y cornisas. Creamos efectos de luz que transforman completamente el ambiente de cualquier estancia.',
    details: ['LED RGB multicolor', 'Control por app o mando', 'Bajo consumo energético', 'Instalación limpia y discreta'],
    image: 'https://public.readdy.ai/ai/img_res/3b3dbca02a72519d7ef4339106ffb842.jpg'
  },
  {
    id: 2,
    title: 'Focos Empotrables & Downlights',
    icon: 'ri-focus-3-line',
    description: 'Diseño y colocación de focos LED empotrables en techos de pladur o escayola. Distribución lumínica estudiada para cada espacio, con opciones regulables y de temperatura de color.',
    details: ['Luz regulable (dimmer)', 'Temperatura cálida o fría', 'Alta eficiencia lumínica', 'Diseño minimalista'],
    image: 'https://readdy.ai/api/search-image?query=modern%20ceiling%20with%20recessed%20LED%20downlights%20in%20contemporary%20home%2C%20clean%20white%20ceiling%20with%20multiple%20spotlights%20creating%20perfect%20illumination%2C%20minimalist%20interior%20design%20with%20professional%20LED%20lighting%20installation%2C%20architectural%20photography%20with%20crisp%20clean%20lighting%2C%20elegant%20modern%20home%20interior&width=600&height=800&seq=led-downlights-2&orientation=portrait'
  },
  {
    id: 3,
    title: 'Iluminación Exterior LED',
    icon: 'ri-sun-line',
    description: 'Proyectos de iluminación exterior para fachadas, jardines, terrazas y accesos. Sistemas resistentes a la intemperie con control automático por sensor o programación horaria.',
    details: ['IP65 resistente al agua', 'Sensor de movimiento', 'Programación horaria', 'Ahorro hasta 80% energía'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20exterior%20LED%20lighting%20on%20modern%20house%20facade%20at%20night%2C%20dramatic%20architectural%20lighting%20with%20warm%20LED%20strips%20highlighting%20building%20features%2C%20contemporary%20home%20exterior%20with%20professional%20outdoor%20illumination%2C%20night%20photography%20with%20rich%20warm%20tones%2C%20elegant%20facade%20lighting%20design&width=600&height=800&seq=led-exterior-3&orientation=portrait'
  }
];

export default function LedLighting() {
  const [activeProject, setActiveProject] = useState(0);

  return (
    <section id="iluminacion-led" className="relative py-16 md:py-32 px-4 md:px-8 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Cabecera */}
        <div className="text-center mb-10 md:mb-20">
          <span className="inline-block border border-gray-600 text-gray-400 px-4 py-2 rounded-lg text-sm mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            (Especialidad)
          </span>
          <h2 className="text-white font-bold text-3xl md:text-6xl mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Diseño de Iluminación
          </h2>
          <h2 className="text-[#FFD700] font-bold text-3xl md:text-6xl mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Interior con LED
          </h2>
          <p className="text-gray-400 text-sm md:text-xl max-w-2xl mx-auto px-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            Transformamos espacios con luz. Diseñamos e instalamos sistemas de iluminación LED a medida que combinan eficiencia energética y estética.
          </p>
        </div>

        {/* Layout principal */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Imagen activa */}
          <div className="relative h-56 sm:h-72 md:h-[600px] rounded-3xl overflow-hidden shadow-2xl">
            {ledProjects.map((project, idx) => (
              <img
                key={project.id}
                src={project.image}
                alt={project.title}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
                  activeProject === idx ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>

            {/* Badge en imagen */}
            <div className="absolute bottom-3 md:bottom-8 left-3 md:left-8 right-3 md:right-8">
              <div className="bg-black/60 backdrop-blur-md border border-[#FFD700]/30 rounded-2xl p-3 md:p-5">
                <div className="flex items-center gap-3 mb-2 md:mb-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0">
                    <div className="bg-[#FFD700] rounded-full w-8 h-8 md:w-10 md:h-10 flex items-center justify-center">
                      <i className={`${ledProjects[activeProject].icon} text-black text-sm md:text-lg`}></i>
                    </div>
                  </div>
                  <h3 className="text-white font-bold text-sm md:text-xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {ledProjects[activeProject].title}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-1 md:gap-2">
                  {ledProjects[activeProject].details.map((detail) => (
                    <div key={detail} className="flex items-center gap-1.5">
                      <i className="ri-check-line text-[#FFD700] text-xs md:text-sm flex-shrink-0"></i>
                      <span className="text-gray-300 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>{detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Selector de proyectos */}
          <div className="space-y-3 md:space-y-6">
            {ledProjects.map((project, idx) => (
              <div
                key={project.id}
                className={`rounded-2xl p-4 md:p-8 cursor-pointer transition-all duration-400 border ${
                  activeProject === idx
                    ? 'bg-[#1E1E1E] border-[#FFD700]/60 shadow-lg shadow-[#FFD700]/10'
                    : 'bg-[#141414] border-gray-800 hover:border-gray-600'
                }`}
                onClick={() => setActiveProject(idx)}
              >
                <div className="flex items-start gap-3 md:gap-5">
                  <div className="w-9 h-9 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0">
                    <div className={`rounded-full w-9 h-9 md:w-12 md:h-12 flex items-center justify-center transition-colors duration-300 ${
                      activeProject === idx ? 'bg-[#FFD700]' : 'bg-gray-800'
                    }`}>
                      <i className={`${project.icon} text-base md:text-xl ${activeProject === idx ? 'text-black' : 'text-gray-400'}`}></i>
                    </div>
                  </div>
                  <div>
                    <h3 className={`font-bold text-base md:text-xl mb-1 md:mb-3 transition-colors duration-300 ${
                      activeProject === idx ? 'text-[#FFD700]' : 'text-white'
                    }`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {project.title}
                    </h3>
                    <p className="text-gray-400 text-xs md:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', lineHeight: '1.7' }}>
                      {project.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <a
              href="#contacto"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#FFD700] text-black px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-base hover:bg-[#FFC700] hover:shadow-2xl hover:shadow-[#FFD700]/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer mt-2"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Pedir presupuesto de iluminación
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
